import type { CSSProperties } from 'react';
import { Avatar, Button, Dropdown, Form, Select, Space, Tag, Tooltip, Typography, type TableColumnType } from 'antd';
import { AppstoreOutlined, CheckOutlined, DownOutlined } from '@ant-design/icons';
import type { ProjectItem } from '@crm/shared';
import { fileUrl } from '../api/client';
import { palette } from '../app/theme';
import { useProjectScope } from '../features/projects/scope';

/** A project's icon, falling back to its full logo, then its initial. */
export function ProjectLogo({ project, size = 20 }: { project: Pick<ProjectItem, 'name' | 'logoFileId' | 'iconFileId'>; size?: number }) {
  const fileId = project.iconFileId ?? project.logoFileId;
  return (
    <Avatar
      shape="square"
      size={size}
      src={fileId ? <img src={fileUrl(fileId)} alt="" style={{ objectFit: 'contain' }} /> : undefined}
      style={{ background: fileId ? 'transparent' : palette.primarySoft, color: palette.primary, fontSize: size * 0.5, flex: 'none' }}
    >
      {project.name[0]?.toUpperCase()}
    </Avatar>
  );
}

/** What project labels show: the acronym (e.g. "SCon"), else the name. */
export const projectLabel = (p: Pick<ProjectItem, 'name' | 'acronym'>) => p.acronym || p.name;

/** Project label shown across the app; nothing while projects are off or the record has none. Hover shows the full name. */
export function ProjectTag({ projectId }: { projectId: string | null | undefined }) {
  const { enabled, byId } = useProjectScope();
  const p = enabled ? byId(projectId) : undefined;
  if (!p) return null;
  return (
    <Tooltip title={p.acronym ? p.name : undefined}>
      <Tag style={{ display: 'inline-flex', alignItems: 'center', gap: 6, paddingInlineStart: 4 }}>
        <ProjectLogo project={p} size={16} />
        {projectLabel(p)}
      </Tag>
    </Tooltip>
  );
}

/** Name plus a muted acronym, for pickers. */
function ProjectName({ project: p }: { project: Pick<ProjectItem, 'name' | 'acronym'> }) {
  return (
    <>
      {p.name}
      {p.acronym && <Typography.Text type="secondary"> · {p.acronym}</Typography.Text>}
    </>
  );
}

/** "Project" table column, only while projects are on. Spread it into a table's columns. */
export function useProjectColumn<T extends { projectId: string | null }>(): TableColumnType<T>[] {
  const { enabled } = useProjectScope();
  return enabled ? [{ title: 'Project', dataIndex: 'projectId', key: 'projectId', width: 180, render: (id: string | null) => <ProjectTag projectId={id} /> }] : [];
}

/** Picker for a project label; null means no project. */
export function ProjectSelect({ value, onChange, ...rest }: { value?: string | null; onChange?: (v: string | null) => void; disabled?: boolean; style?: CSSProperties }) {
  const { projects } = useProjectScope();
  return (
    <Select
      {...rest}
      allowClear
      showSearch
      optionFilterProp="label"
      placeholder="No project"
      value={value ?? undefined}
      onChange={(v) => onChange?.((v as string | undefined) ?? null)}
      options={projects.map((p) => ({
        value: p.id,
        label: p.name,
        title: p.name,
      }))}
      optionRender={(o) => {
        const p = projects.find((x) => x.id === o.value);
        return p ? (
          <Space size={8}>
            <ProjectLogo project={p} />
            <span>
              <ProjectName project={p} />
            </span>
          </Space>
        ) : (
          o.label
        );
      }}
    />
  );
}

/** "Project" form field for tag, list and segment forms; hidden until the workspace has two or more projects. */
export function ProjectFormItem({ extra }: { extra?: string }) {
  const { enabled } = useProjectScope();
  if (!enabled) return null;
  return (
    <Form.Item name="projectId" label="Project" extra={extra}>
      <ProjectSelect />
    </Form.Item>
  );
}

/** Header selector: all projects (the whole CRM) or one project's data. Hidden with fewer than two projects. */
export function ProjectSwitcher() {
  const { enabled, projects, current, select } = useProjectScope();
  if (!enabled) return null;
  const check = (on: boolean) => <CheckOutlined style={{ visibility: on ? 'visible' : 'hidden', color: palette.primary }} />;
  return (
    <Dropdown
      trigger={['click']}
      placement="bottomRight"
      menu={{
        selectedKeys: [current?.id ?? 'all'],
        onClick: ({ key }) => select(key === 'all' ? null : key),
        items: [
          {
            key: 'all',
            label: (
              <Space size={8}>
                <Avatar shape="square" size={20} icon={<AppstoreOutlined />} style={{ background: palette.primarySoft, color: palette.primary }} />
                <span style={{ flex: 1 }}>All projects</span>
                {check(!current)}
              </Space>
            ),
          },
          { type: 'divider' },
          ...projects.map((p) => ({
            key: p.id,
            label: (
              <Space size={8}>
                <ProjectLogo project={p} />
                <span style={{ flex: 1 }}>
                  <ProjectName project={p} />
                </span>
                {check(current?.id === p.id)}
              </Space>
            ),
          })),
        ],
      }}
    >
      <Button aria-label="Project" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, maxWidth: 240 }}>
        {current ? <ProjectLogo project={current} /> : <AppstoreOutlined />}
        <Typography.Text ellipsis style={{ maxWidth: 160 }}>
          {current?.name ?? 'All projects'}
        </Typography.Text>
        <DownOutlined style={{ fontSize: 10 }} />
      </Button>
    </Dropdown>
  );
}
